import useSWR from "swr";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { fetcher } from "../../util/axios";
import PageSpinner from "../../components/PageSpinner";
import placeholder from "../../util/placeholder";
import { useCurrency } from "../../hooks/useCurrency";
import { Button } from "../../components/ui/button";
import { ChevronLeft, Loader, Share } from "lucide-react";
import { handleMutation } from "../../util/axios";
import requestErrorHandler from '../../util/requestErrorHandler';
import useSWRMutation from "swr/mutation";
import { message } from "antd";

export default function VehicleDetailsPage() {
  const { id } = useParams();
  const [formatCurrency] = useCurrency();
  const navigate = useNavigate();

  const { data, isLoading } = useSWR(id ? { url: `fleet/${id}/details` } : null, fetcher);

  const { data: history, isLoading: isLoadingHistory, mutate } = useSWR(id ? { url: `fleet/${id}/history` } : null, fetcher);

  const vehicle: any = data?.data?.vehicleDetails;

  const { trigger: triggerTakeOffline, isMutating } = useSWRMutation(
    `/fleet/${id}/take-off-line`,
    handleMutation
  );

  const takeOffline = async () => {
    try {
      const res = await triggerTakeOffline({
        status: 'offline'
      });
      mutate();
      console.log(res);
      if (res.success) {
        message.success(res.message);
      }
    } catch (error) {
      requestErrorHandler(error);
    }
  }

  const shareVehicleLink = () => {
    const link = `${process.env.REACT_APP_RENTER_URL!}/rent/vehicle/${vehicle.publicSlug}`;
    if (navigator.share) {
      navigator.share({
        title: `${vehicle?.truckCategoryDetails?.name}`,
        text: 'Check out this vehicle',
        url: link,
      });
    } else {
      navigator.clipboard.writeText(link);
    }
    message.success("Vehicle link copied to clipboard!");
  }

  if (isLoading) return <PageSpinner />;

  const rentalHistory = history?.data || [];

  return (
    <div className="h-full flex flex-col overflow-hidden border rounded-lg">
      <div className="p-2 border-b flex items-center justify-between">
        <div className="flex items-center">
          <Button variant="ghost" className="text-gray-500 hover:text-blue-600 px-2 py-1" onClick={() => navigate(-1)}>
            <ChevronLeft className="!size-6" />
          </Button>
          <h2 className="text-sm font-semibold">
            Vehicle Overview - {" "}
            <span className="text-blue-600 font-semibold">{vehicle?.regPlate}</span>
          </h2>
        </div>
      </div>
      <div className="flex-1 flex flex-col overflow-auto bg-white rounded p-4">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
                <div className="size-16 border rounded flex items-center justify-center bg-gray-50">
                    <img
                        src={vehicle?.truckCategoryDetails?.image || placeholder}
                        alt="truck"
                        className="object-contain size-10"
                    />
                </div>
                <div>
                    <p className="text-sm font-semibold">{vehicle?.truckCategoryDetails?.name}</p>
                    <p className="text-blue-600 font-semibold text-sm hover:underline">{vehicle?.status}</p>
                </div>
                <div className="ml-10 text-xs text-gray-500">
                    <p className="font-medium">75% Compliant</p>
                    <p className="font-medium">Created: {dayjs(vehicle?.createdAt).format("DD/MM/YYYY")}</p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  className="text-sm font-semibold text-primary"
                  onClick={() => navigate(`/vehicles/${vehicle?.id}/modify-vehicle`, {
                    state: { vehicle }
                  })}
                >
                  Modify vehicle
                </Button>
                <Button
                  className="bg-orange-400 text-white"
                  disabled={isMutating}
                  onClick={takeOffline}
                >
                  {isMutating ? <Loader /> : 'Take Offline'}
                </Button>
            </div>
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
          {/* Left column (md: col-span-7) */}
          <div className="md:col-span-7 h-full flex flex-col">
            <div className="mt-6 space-y-6">
              <div>
                <h3 className="text-xs font-semibold">Driver information</h3>
                <p className="text-sm text-gray-500 font-medium">{vehicle?.driverName}</p>
                <p className="text-xs text-gray-500 font-medium">{vehicle?.driverPhone}</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold">Trip Overview</h3>
                <p className="text-sm text-gray-500 font-medium">{vehicle?.vehicleHistory?.totalTripsAssigned} Trips</p>
                <p className="text-sm text-gray-500 font-medium">{vehicle?.vehicleHistory?.totalCompletedTrips} Trips Completed</p>
                <p className="text-orange-500 font-semibold mt-1">{formatCurrency(+vehicle?.totalEarned)}</p>
              </div>
            </div>
            {vehicle?.publicSlug && (
              <div className="mt-4">
                <Button className="text-sm font-semibold" onClick={shareVehicleLink}>
                  Share Link
                  <Share className="!size-4 ml-2" />
                </Button>
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
                <p className="text-sm font-medium">Tracking data available</p>
                <Button variant="ghost" className="text-blue-600 text-sm hover:underline !pb-0">Expand</Button>
            </div>
            <div className="h-48 md:flex-1 w-full rounded overflow-hidden border">
                <img
                  src={vehicle?.mapImage || "/assets/placeholder-map.png"}
                  alt="map"
                  className="w-full h-full object-cover"
                />
            </div>
          </div>

          {/* Right column (md: col-span-5) */}
          <div className="md:col-span-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold">Rental History</span>
              <button className="text-blue-600 text-sm hover:underline font-semibold">See all</button>
            </div>

            <div className="space-y-4">
              {rentalHistory.map((r: any, idx: number) => (
                <div
                    key={idx}
                    className="p-3 bg-white rounded shadow-lg shadow-sky-200 cursor-pointer"
                    onClick={() => navigate(`/vehicles/${vehicle?.id}/history/${r.id}`, {
                      state: { vehicle }
                    })}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{r.referenceId}</p>
                    <p className="text-xs text-gray-500 font-semibold">{dayjs(r.deliveryDate).format("DD/MM/YYYY")}</p>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4 text-sm">
                    <p className="text-orange-500 text-xs font-semibold">{formatCurrency(r.totalPrice)}</p>
                    <p className="text-gray-500 text-xs font-medium">{r.noOfTrips} Trips</p>
                    <p className="text-gray-500 text-xs font-medium">{r.dropoffAddress}</p>
                  </div>
                </div>
              ))}
              {rentalHistory.length === 0 && !isLoadingHistory && (
                <p className="text-center text-gray-500 text-xs font-medium mt-10">No rental history available.</p>
              )}
              {rentalHistory.length === 0 && isLoadingHistory && (<div className="w-full flex justify-center items-center p-6">
                  <Loader className="animate-spin text-black" />
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
